import React, { useState, useEffect } from 'react';
import { Card, CardContent } from './ui/card';
import { Progress } from './ui/progress';
import { Skeleton } from './ui/skeleton';
import { prometheusService, AgentMetrics } from '../services/prometheusService';
import { 
  Activity, 
  TrendingUp, 
  TrendingDown, 
  Zap, 
  DollarSign, 
  Cpu, 
  HardDrive,
  Timer,
  AlertCircle
} from 'lucide-react';

interface AgentMetricsCardProps {
  agentName: string;
  compact?: boolean;
}

const formatBytes = (bytes: number): string => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
};

const formatDuration = (seconds: number): string => {
  if (seconds < 60) return `${Math.round(seconds)}s`;
  if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
  if (seconds < 86400) return `${Math.round(seconds / 3600)}h`;
  return `${Math.round(seconds / 86400)}d`;
};

const MetricItem: React.FC<{
  icon: React.ReactNode;
  label: string;
  value: string | number;
  unit?: string;
  trend?: 'up' | 'down' | 'neutral';
  compact?: boolean;
}> = ({ icon, label, value, unit, trend, compact }) => {
  const trendIcon = trend === 'up' ? <TrendingUp className="h-3 w-3 text-green-500" /> :
                   trend === 'down' ? <TrendingDown className="h-3 w-3 text-red-500" /> : null;

  if (compact) {
    return (
      <div className="flex items-center justify-between py-1">
        <div className="flex items-center gap-1.5">
          {icon}
          <span className="text-xs text-gray-600">{label}</span>
        </div>
        <div className="flex items-center gap-1">
          <span className="text-sm font-medium">
            {typeof value === 'number' ? value.toFixed(2) : value}
          </span>
          {unit && <span className="text-xs text-gray-500">{unit}</span>}
          {trendIcon}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-1">
      <div className="flex items-center gap-2 text-sm text-gray-600">
        {icon}
        <span>{label}</span>
      </div>
      <div className="flex items-center gap-2">
        <span className="text-lg font-semibold">
          {typeof value === 'number' ? value.toFixed(2) : value}
        </span>
        {unit && <span className="text-sm text-gray-500">{unit}</span>}
        {trendIcon}
      </div>
    </div>
  );
};

export const AgentMetricsCard: React.FC<AgentMetricsCardProps> = ({ agentName, compact = false }) => {
  const [metrics, setMetrics] = useState<AgentMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchMetrics = async () => {
      try {
        setLoading(true);
        const data = await prometheusService.getAgentMetrics(agentName);
        setMetrics(data);
        setError(null);
      } catch (err) {
        console.error('Error fetching agent metrics:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch metrics');
      } finally {
        setLoading(false);
      }
    };

    fetchMetrics();
    const interval = setInterval(fetchMetrics, 30000); // Refresh every 30 seconds

    return () => clearInterval(interval);
  }, [agentName]);

  if (loading) {
    return (
      <Card className={compact ? 'p-3' : ''}>
        <CardContent className={compact ? 'p-0' : 'p-4'}>
          <div className="space-y-2">
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-4 w-1/2" />
            <Skeleton className="h-4 w-2/3" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error || !metrics) {
    return (
      <Card className={compact ? 'p-3' : ''}>
        <CardContent className={compact ? 'p-0' : 'p-4'}>
          <div className="flex items-center gap-2 text-sm text-red-600">
            <AlertCircle className="h-4 w-4" />
            <span>Metrics unavailable</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (compact) {
    return (
      <div className="space-y-1">
        <div className="grid grid-cols-2 gap-2">
          <MetricItem
            icon={<Activity className="h-3 w-3" />}
            label="Rate"
            value={metrics.processingRate}
            unit="msg/s"
            compact
          />
          <MetricItem
            icon={<Zap className="h-3 w-3" />}
            label="Success"
            value={metrics.successRate}
            unit="%"
            compact
          />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <MetricItem
            icon={<Timer className="h-3 w-3" />}
            label="Resp"
            value={metrics.avgResponseTime}
            unit="s"
            compact
          />
          <MetricItem
            icon={<DollarSign className="h-3 w-3" />}
            label="Cost"
            value={metrics.costPerHour}
            unit="$/h"
            compact
          />
        </div>
        {metrics.kafkaLag > 100 && (
          <div className="flex items-center gap-1 text-xs text-yellow-600">
            <AlertCircle className="h-3 w-3" />
            <span>Queue lag: {metrics.kafkaLag}</span>
          </div>
        )}
      </div>
    );
  }

  return (
    <Card>
      <CardContent className="p-6">
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <MetricItem
              icon={<Activity className="h-4 w-4" />}
              label="Processing Rate"
              value={metrics.processingRate}
              unit="msg/s"
            />
            <MetricItem
              icon={<Zap className="h-4 w-4" />}
              label="Success Rate"
              value={metrics.successRate}
              unit="%"
              trend={metrics.successRate >= 99 ? 'up' : metrics.successRate < 95 ? 'down' : 'neutral'}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <MetricItem
              icon={<Timer className="h-4 w-4" />}
              label="Avg Response Time"
              value={metrics.avgResponseTime}
              unit="s"
            />
            <MetricItem
              icon={<Activity className="h-4 w-4" />}
              label="Token Usage"
              value={metrics.tokensPerMinute}
              unit="tokens/min"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <MetricItem
              icon={<DollarSign className="h-4 w-4" />}
              label="Cost Rate"
              value={metrics.costPerHour}
              unit="$/hour"
            />
            <MetricItem
              icon={<Activity className="h-4 w-4" />}
              label="Cache Hit Rate"
              value={metrics.cacheHitRate}
              unit="%"
            />
          </div>

          <div className="space-y-2 pt-2 border-t">
            <div className="flex items-center justify-between text-sm">
              <span className="flex items-center gap-2">
                <Cpu className="h-4 w-4" />
                CPU Usage
              </span>
              <span>{metrics.cpuUsage.toFixed(1)}%</span>
            </div>
            <Progress value={metrics.cpuUsage} className="h-2" />

            <div className="flex items-center justify-between text-sm">
              <span className="flex items-center gap-2">
                <HardDrive className="h-4 w-4" />
                Memory Usage
              </span>
              <span>{formatBytes(metrics.memoryUsage)}</span>
            </div>
            <Progress value={Math.min((metrics.memoryUsage / (512 * 1024 * 1024)) * 100, 100)} className="h-2" />
          </div>

          {metrics.kafkaLag > 0 && (
            <div className="pt-2 border-t">
              <div className="flex items-center justify-between text-sm">
                <span className="flex items-center gap-2 text-yellow-600">
                  <AlertCircle className="h-4 w-4" />
                  Kafka Queue Lag
                </span>
                <span className="font-medium">{metrics.kafkaLag} messages</span>
              </div>
            </div>
          )}

          <div className="text-xs text-gray-500 text-right">
            Uptime: {formatDuration(metrics.uptime)}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};